import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { capitalizeFirstLetter } from '@common/react/utils/utils';
import { parseQuery } from '@common/typescript/utils/url';
import SimpleSearchInput from '@common/react/components/Forms/SimpleSearchInput/SimpleSearchInput';
import {
	defaultTransformFiltersBeforeHandleUrl,
	ItemsProvider,
	useItemsProviderContext,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import ServerPageProvider from '@common/react/components/UI/lang/SeverPageProviderWithLang/ServerpageProviderWithLang';
import loadable from '@common/react/loadable/loadableSettings';

import { Faq } from '@commonTuna/react/objects/Faq';

import ShareLinks from '@app/components/UI/ShareLinks/ShareLinks';
import CollapseService from '@app/components/Pages/Faq/CollapseService';
import { selectProps } from '@app/components/Utils';

import '@app/scss/pages/faqPage.scss';

const Pagination = loadable(() => import(/* webpackChunkName: "Pagination" */ '@common/react/components/base/Pagination/Pagination'));

interface FaqListPageInnerProps {
	title: string;
}

const FaqListPageInner: React.FC<FaqListPageInnerProps> = ({
	title,
}) => {
	const {
		state: {
			items, filters, pagination, loading,
		},
		actions: { handleChange, reload },
	} = useItemsProviderContext<Faq>();

	const { t, i18n } = useTranslation();
	const langName = capitalizeFirstLetter(i18n.language);

	return <>
		<SimpleSearchInput
			name="text"
			containerClassName="dark-search"
			placeholder={t('forms.search')}
			onSubmit={handleChange}
			defaultValue={filters.text}
			withoutForm
		/>
		{items
			&& <div className="service-page__block is-relative">
				{loading && <div className="loader-container">
					<Loader />
				</div>}
				<CollapseService items={items} langName={langName} search={filters.text} hideEmpty={false} />
				{pagination.total > 10
					&& <Pagination
						fallback={<></>}
						current={pagination.current}
						className="dark-pagination"
						pageSize={pagination.pageSize}
						total={pagination.total ? pagination.total : 0}
						onChange={(page, pageSize) => reload({ current: page, pageSize })}
						selectProps={selectProps}
					/>
				}
			</div>
		}
	</>;
};

const FaqListPage: React.FC = () => {
	const { t } = useTranslation();

	const location = useLocation();

	const search = parseQuery(location.search);
	const defCurrent = +search.page || 1;
	const title = t('site.Faqs');

	return (
		<div className="faq-page inner-page container">
			<div className="clearfix">
				<BreadCrumbs title={title} />
				<ShareLinks />
			</div>
			<div className="inner-page__headline text-center">
				<h1>{t('site.Faqs')}</h1>
			</div>
			<ServerPageProvider
				path="faq"
				inner={(serverPage) =>
					<ItemsProvider
						items={serverPage?.items?.list}
						pagination={{
							current: defCurrent, pageSize: 10, total: serverPage?.items?.count,
						}}
						loadRequest="faqViewListRemote"
						filters={{ page: defCurrent, withRows: true }}
						withHandleUrl
						pageInSearch
						transformFiltersBeforeHandleUrl={(filters) => ({
							...defaultTransformFiltersBeforeHandleUrl(filters),
							page: filters.page || 1,
						})}
					>
						<FaqListPageInner
							title={title}
						/>
					</ItemsProvider>
				}
			/>
		</div>
	);
};

export default FaqListPage;
